import { useEffect, useState } from 'react'
import { api } from '../api'
import {
  Badge,
  BarChart,
  CHART_COLORS,
  Empty,
  Notice,
  Page,
  Panel,
  StatTile,
  Table,
  TileRow,
  fmtDuration,
  fmtNumber,
  fmtTime,
} from '../components/ui'
import type {
  AgentPerf,
  CampaignPerf,
  ComplianceReport,
  HourlyPoint,
  Overview,
} from '../types'

const today = () => new Date().toISOString().slice(0, 10)
const daysAgo = (n: number) =>
  new Date(Date.now() - n * 86400000).toISOString().slice(0, 10)

type Tab = 'summary' | 'campaigns' | 'agents' | 'compliance'

export default function Reports() {
  const [tab, setTab] = useState<Tab>('summary')
  const [from, setFrom] = useState(today())
  const [to, setTo] = useState(today())

  const [overview, setOverview] = useState<Overview | null>(null)
  const [hourly, setHourly] = useState<HourlyPoint[]>([])
  const [dispositions, setDispositions] = useState<{ code: string; label: string; count: number }[]>([])
  const [campaigns, setCampaigns] = useState<CampaignPerf[]>([])
  const [agents, setAgents] = useState<AgentPerf[]>([])
  const [compliance, setCompliance] = useState<ComplianceReport | null>(null)

  useEffect(() => {
    const p = { from, to }
    api.overview(p).then(setOverview).catch(() => setOverview(null))
    api.hourly(p).then(setHourly).catch(() => setHourly([]))
    api.dispositionBreakdown(p).then(setDispositions).catch(() => setDispositions([]))
    api.campaignPerformance(p).then(setCampaigns).catch(() => setCampaigns([]))
    api.agentPerformance(p).then(setAgents).catch(() => setAgents([]))
    api.compliance(p).then(setCompliance).catch(() => setCompliance(null))
  }, [from, to])

  const preset = (days: number) => {
    setFrom(daysAgo(days))
    setTo(today())
  }

  return (
    <Page
      title="Reports"
      subtitle="Performance, outcomes and compliance"
      actions={
        <div className="range-picker">
          <button className="chip" onClick={() => { setFrom(today()); setTo(today()) }}>Today</button>
          <button className="chip" onClick={() => preset(7)}>7 days</button>
          <button className="chip" onClick={() => preset(30)}>30 days</button>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          <span className="muted">to</span>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
      }
    >
      <div className="tabs">
        {(['summary', 'campaigns', 'agents', 'compliance'] as Tab[]).map((t) => (
          <button key={t} className={tab === t ? 'tab on' : 'tab'} onClick={() => setTab(t)}>
            {t}
          </button>
        ))}
      </div>

      {tab === 'summary' && (
        <>
          <TileRow>
            <StatTile label="Attempts" value={fmtNumber(overview?.attempts ?? 0)} />
            <StatTile label="Answered" value={fmtNumber(overview?.answered ?? 0)} sub={`${overview?.answer_rate ?? 0}%`} />
            <StatTile label="Connects" value={fmtNumber(overview?.connects ?? 0)} tone="live" />
            <StatTile label="Contacts" value={fmtNumber(overview?.contacts ?? 0)} sub={`${overview?.contact_rate ?? 0}%`} />
            <StatTile label="Sales" value={fmtNumber(overview?.sales ?? 0)} tone="ok" sub={`${overview?.conversion_rate ?? 0}%`} />
            <StatTile label="Avg talk" value={fmtDuration(overview?.avg_talk_sec ?? 0)} />
          </TileRow>

          <Panel title="By hour">
            <BarChart
              height={220}
              data={hourly.map((h) => ({
                label: fmtTime(h.hour),
                values: [
                  { value: h.attempts, color: CHART_COLORS.attempts, name: 'Attempts' },
                  { value: h.answered, color: CHART_COLORS.answered, name: 'Answered' },
                  { value: h.connects, color: CHART_COLORS.connects, name: 'Connects' },
                  { value: h.contacts, color: CHART_COLORS.contacts, name: 'Contacts' },
                ],
              }))}
            />
          </Panel>

          <Panel title="Outcomes">
            {dispositions.length === 0 ? (
              <Empty message="No dispositioned calls in this range." />
            ) : (
              <Table head={['Code', 'Outcome', 'Count', 'Share']}>
                {(() => {
                  const total = dispositions.reduce((a, d) => a + d.count, 0)
                  return dispositions.map((d) => (
                    <tr key={d.code}>
                      <td className="mono">{d.code}</td>
                      <td>{d.label}</td>
                      <td>{fmtNumber(d.count)}</td>
                      <td>
                        <div className="bar-cell">
                          <span
                            className="bar-fill"
                            style={{ width: `${total ? (d.count / total) * 100 : 0}%` }}
                          />
                          <span className="bar-text">
                            {total ? ((d.count / total) * 100).toFixed(1) : 0}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))
                })()}
              </Table>
            )}
          </Panel>
        </>
      )}

      {tab === 'campaigns' && (
        <Panel title="Campaign performance">
          {campaigns.length === 0 ? (
            <Empty message="No campaign activity in this range." />
          ) : (
            <Table
              head={['Campaign', 'Mode', 'Attempts', 'Answered', 'Connects', 'Contacts', 'Sales',
                     'Answer %', 'Contact %', 'Conv %', 'Abandon %', 'Avg talk']}
            >
              {campaigns.map((c) => (
                <tr key={c.id}>
                  <td className="strong">{c.name}</td>
                  <td><Badge tone="muted">{c.dial_mode}</Badge></td>
                  <td>{fmtNumber(c.attempts)}</td>
                  <td>{fmtNumber(c.answered)}</td>
                  <td>{fmtNumber(c.connects)}</td>
                  <td>{fmtNumber(c.contacts)}</td>
                  <td>{fmtNumber(c.sales)}</td>
                  <td>{c.answer_rate}%</td>
                  <td>{c.contact_rate}%</td>
                  <td>{c.conversion_rate}%</td>
                  <td className={c.abandon_rate >= 3 ? 'danger-text strong' : ''}>
                    {c.abandon_rate}%
                  </td>
                  <td className="mono">{fmtDuration(c.avg_talk_sec)}</td>
                </tr>
              ))}
            </Table>
          )}
        </Panel>
      )}

      {tab === 'agents' && (
        <Panel title="Agent performance">
          {agents.length === 0 ? (
            <Empty message="No agent activity in this range." />
          ) : (
            <Table
              head={['Agent', 'Calls', 'Contacts', 'Sales', 'Contact %', 'Conv %',
                     'Avg talk', 'Total talk', 'Waiting', 'Paused', 'Occupancy']}
            >
              {agents.map((a) => (
                <tr key={a.id}>
                  <td>
                    <span className="strong">{a.full_name}</span>
                    <div className="muted small">{a.username}</div>
                  </td>
                  <td>{fmtNumber(a.calls)}</td>
                  <td>{fmtNumber(a.contacts)}</td>
                  <td>{fmtNumber(a.sales)}</td>
                  <td>{a.contact_rate}%</td>
                  <td>{a.conversion_rate}%</td>
                  <td className="mono">{fmtDuration(a.avg_talk_sec)}</td>
                  <td className="mono">{fmtDuration(a.total_talk_sec)}</td>
                  <td className="mono muted">{fmtDuration(a.wait_sec)}</td>
                  <td className="mono muted">{fmtDuration(a.pause_sec)}</td>
                  <td>
                    <div className="bar-cell">
                      <span className="bar-fill" style={{ width: `${a.occupancy_pct}%` }} />
                      <span className="bar-text">{a.occupancy_pct}%</span>
                    </div>
                  </td>
                </tr>
              ))}
            </Table>
          )}
        </Panel>
      )}

      {tab === 'compliance' && compliance && (
        <>
          {compliance.chain_intact ? (
            <Notice kind="success">
              The compliance decision log verifies intact. Every call placed has a recorded reason,
              and the chain shows no edits or deletions.
            </Notice>
          ) : (
            <Notice kind="error">
              The decision log fails verification at entry {compliance.chain_broken_at?.id}. Rows
              have been altered or removed after the fact — investigate before relying on this log.
            </Notice>
          )}

          <Panel title="Abandonment — rolling 30 days">
            {compliance.abandonment_30d.length === 0 ? (
              <Empty
                message="No automated dialing yet."
                hint="Only power and predictive campaigns can abandon calls, so only they are measured here."
              />
            ) : (
              <Table head={['Campaign', 'Live answers', 'Abandoned', 'Rate', 'Status']}>
                {compliance.abandonment_30d.map((a) => (
                  <tr key={a.campaign_id}>
                    <td className="strong">{a.campaign}</td>
                    <td>{fmtNumber(a.answered_live)}</td>
                    <td>{fmtNumber(a.abandoned)}</td>
                    <td className={a.status === 'over' ? 'danger-text strong' : ''}>
                      {a.abandon_pct}%
                    </td>
                    <td>
                      <Badge tone={a.status === 'over' ? 'danger' : a.status === 'warning' ? 'warn' : 'ok'}>
                        {a.status === 'over' ? 'over the 3% ceiling' : a.status === 'warning' ? 'approaching' : 'within limits'}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </Table>
            )}
          </Panel>

          <Panel
            title="Gate decisions"
            actions={<span className="muted small">every dial attempt, allowed or refused</span>}
          >
            {compliance.decisions.length === 0 ? (
              <Empty message="No decisions recorded in this range." />
            ) : (
              <Table head={['Decision', 'Reason', 'Count']}>
                {compliance.decisions.map((d, i) => (
                  <tr key={i}>
                    <td>
                      <Badge tone={d.decision === 'allow' ? 'ok' : d.decision === 'block' ? 'danger' : 'warn'}>
                        {d.decision}
                      </Badge>
                    </td>
                    <td className="mono">{d.reason_code}</td>
                    <td>{fmtNumber(d.count)}</td>
                  </tr>
                ))}
              </Table>
            )}
          </Panel>
        </>
      )}
    </Page>
  )
}
